<script setup lang="ts">
import { ref } from 'vue';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
  duration: number;
}

const toasts = ref<Toast[]>([]);
let nextId = 0;

const colors = {
  success: '#86efac', // Light green
  error: '#fca5a5', // Light red
  info: '#93c5fd', // Light blue
};

function show(message: string, type: Toast['type'] = 'info', duration = 3000) {
  const id = nextId++;
  const toast: Toast = {
    id,
    message,
    type,
    duration,
  };
  toasts.value.push(toast);

  // Auto-remove after duration
  setTimeout(() => {
    remove(id);
  }, duration);

  return id;
}

function remove(id: number) {
  const index = toasts.value.findIndex(t => t.id === id);
  if (index > -1) {
    toasts.value.splice(index, 1);
  }
}

// Expose methods to be used globally
const toastApi = {
  show,
  success: (message: string, duration?: number) => show(message, 'success', duration),
  error: (message: string, duration?: number) => show(message, 'error', duration),
  info: (message: string, duration?: number) => show(message, 'info', duration),
  remove,
};

// Make it available globally
if (typeof window !== 'undefined') {
  (window as any).$toast = toastApi;
}

// Generate random rotation for each toast
function getRotation(index: number) {
  return ((index % 3) - 1) * 2; // -2, 0, or 2 degrees
}

defineExpose(toastApi);
</script>

<template>
  <Teleport to="body">
    <div class="fixed top-20 right-4 z-[100] flex flex-col gap-4 pointer-events-none">
      <TransitionGroup
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 translate-x-8 rotate-6"
        enter-to-class="opacity-100 translate-x-0 rotate-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 translate-x-0"
        leave-to-class="opacity-0 translate-x-8"
      >
        <div
          v-for="(toast, index) in toasts"
          :key="toast.id"
          class="relative pointer-events-auto"
          :style="{ transform: `rotate(${getRotation(index)}deg)` }"
        >
          <!-- Post-it toast -->
          <div
            class="relative rounded shadow-lg p-5 pr-10 max-w-[340px]"
            :style="{ backgroundColor: colors[toast.type] }"
          >
            <!-- Paper texture overlay -->
            <div
              class="absolute inset-0 rounded opacity-30 pointer-events-none"
              style="
                background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E');
                mix-blend-mode: multiply;
              "
            ></div>

            <!-- Tape strip -->
            <div
              class="absolute -top-2 left-4 w-12 h-5 bg-[rgba(255,255,255,0.5)] rounded-sm shadow-sm"
              :style="{ transform: `rotate(${getRotation(index) * 0.5}deg)` }"
            ></div>

            <!-- Close button -->
            <button
              @click="remove(toast.id)"
              class="absolute top-2 right-2 p-1.5 text-gray-700 hover:text-gray-900 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <!-- Message -->
            <p class="relative z-10 text-base font-medium text-gray-800">{{ toast.message }}</p>

            <!-- Progress bar -->
            <div
              class="absolute bottom-0 left-0 right-0 h-0.5 bg-black/10 rounded-b-sm overflow-hidden"
            >
              <div
                class="h-full bg-black/20"
                :style="{
                  animation: `shrink ${toast.duration}ms linear forwards`,
                }"
              ></div>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>

    <!-- Animation keyframes -->
    <component :is="'style'">
      @keyframes shrink { from { width: 100%; } to { width: 0%; } }
    </component>
  </Teleport>
</template>
